import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Sticky banner that surfaces social_error query params from OAuth round-trips.
 *
 * Every OAuth failure path the backend or frontend OAuth handler can hit
 * appends ?social_error=<code>&detail=<optional> to whatever page the
 * browser lands on. Before this component, those just sat silently in the
 * URL bar — the user bounced back to /dashboard or / and saw the same UI
 * they came from, with no indication the connect attempt failed.
 *
 * Decodes the well-known error codes into friendly text + an action
 * suggestion. Unknown codes fall back to "(code)" with a "contact support"
 * suggestion. Dismiss button strips both query params from the URL so a
 * refresh doesn't re-show the banner.
 *
 * Mounted globally in App.jsx (renders on every route). Auth-agnostic —
 * works whether the user lands on /, /dashboard, or deep into the app.
 */
const ERROR_DECODER = {
  missing_token_fragment: {
    headline: 'Instagram connect was interrupted',
    body: 'Facebook didn\'t pass back the authorization token. This usually means the OAuth window was closed before consent finished. Try again from the project page.',
  },
  missing_token_or_state: {
    headline: 'Instagram connect was interrupted',
    body: 'The OAuth response was missing some data. Try again — if it keeps happening, sign out of Facebook in this browser first.',
  },
  missing_code_or_state: {
    headline: 'Instagram connect didn\'t complete',
    body: 'Facebook didn\'t pass back the authorization code. Try again from the project page.',
  },
  invalid_or_expired_state: {
    headline: 'Your connect link expired',
    body: 'OAuth states live for 10 minutes. Click Connect Instagram again to start a fresh round-trip.',
  },
  unsupported_platform: {
    headline: 'Unsupported platform',
    body: 'That platform isn\'t wired up yet. Currently Instagram and LinkedIn are supported.',
  },
  oauth_token_failed: {
    headline: 'Couldn\'t save your Instagram connection',
    body: 'We received the token from Facebook but couldn\'t persist it on our side. Usually a transient error — try Connect Instagram again. If it persists, contact support.',
  },
  connect_failed: {
    headline: 'Instagram connect failed',
    body: 'Something on our side broke during the OAuth round-trip. Try again in a moment.',
  },
  no_ig_business_account: {
    headline: 'No Instagram Business account linked',
    body: 'The Facebook account you signed in with admins a Page, but that Page isn\'t linked to an Instagram Business or Creator account. Open Instagram on your phone → Settings → Account type → switch to Professional → Business, and during setup link the Facebook Page. Then click Connect Instagram again.',
  },
  token_decrypt_failed: {
    headline: 'Token storage error',
    body: 'We couldn\'t encrypt your access token at rest. Operator action required — contact support.',
  },
  workers_request_failed: {
    headline: 'Workers connection failed',
    body: 'The background-jobs service didn\'t respond. Try again in a moment; if it keeps happening, the operator needs to check the workers container.',
  },
  // Facebook-side errors echoed back through `error` param
  access_denied: {
    headline: 'You declined the permissions',
    body: 'The Facebook consent screen needs every requested permission for Markaii to publish. Click Connect Instagram again and accept all permissions.',
  },
  user_denied: {
    headline: 'You declined the permissions',
    body: 'The Facebook consent screen needs every requested permission for Markaii to publish. Click Connect Instagram again and accept all permissions.',
  },
};

export default function SocialErrorBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorCode, setErrorCode] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('social_error');
    if (code) {
      setErrorCode(code);
      setDetail(params.get('detail'));
    } else {
      setErrorCode(null);
      setDetail(null);
    }
  }, [location.search]);

  if (!errorCode) return null;

  const decoded = ERROR_DECODER[errorCode] || {
    headline: 'Connection failed',
    body: `Error code: ${errorCode}. Try again, or contact support if it persists.`,
  };

  function dismiss() {
    // Strip social_error + detail from the URL without changing the page
    const params = new URLSearchParams(location.search);
    params.delete('social_error');
    params.delete('detail');
    const next = params.toString();
    navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
    setErrorCode(null);
    setDetail(null);
  }

  return (
    <div className="social-error-banner" role="alert">
      <div className="social-error-banner-body">
        <strong>{decoded.headline}</strong>
        <span>{decoded.body}</span>
        {detail && detail !== errorCode && (
          <span className="social-error-banner-detail">Details: {detail}</span>
        )}
      </div>
      <button
        type="button"
        className="btn-small social-error-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss error"
      >
        Dismiss
      </button>
    </div>
  );
}
